import { useState, useEffect, useMemo, useContext } from "react";
import TagManager from 'react-gtm-module';
import CookieConsent from "react-cookie-consent";
import { track } from "utils/track";

import 'assets/css/style.css';


// react-router components

import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import GlobalStyles from "@mui/material/GlobalStyles";

import MDBox from "components/MDBox";

import Sidenav from "examples/Sidenav";
import BottomNav from "examples/BottomNav";
import Configurator from "examples/Configurator";
import UpgradeModal from "examples/UpgradeModal";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// import routes from "routes";
import generateRoutes from './routes';

import { 
  useMaterialUIController, 
  setMiniSidenav, 
  setWhiteSidenav, 
  setFixedNavbar, 
  setDarkMode,
  setSidenavColor
} from "context";

// Images
import brandWhite from "assets/images/logo-white.png";
import brandColor from "assets/images/logo-color.png";

import { setupAxiosInterceptors } from "./services/interceptor";
import ProtectedRoute from "examples/ProtectedRoute";
import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";
import Login from "auth/login";
import Register from "auth/register";
import { AuthContext } from "context";
import Swal from "sweetalert2";

const GTM_ID = 'GTM-WPGB5JHJ';
const CONSENT_COOKIE = 'djparafestas_lgpd_consent';

export default function App() {
  const authContext = useContext(AuthContext);
  const routes = useMemo(() => generateRoutes(authContext.user), [authContext.user]);

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname, search } = useLocation();

  // Google Tag Manager só com consentimento (mesma lógica da vitrine). Eventos enviados
  // antes disso ficam na fila do dataLayer e só são lidos se o GTM for carregado.
  useEffect(() => {
    const hasConsent = document.cookie.split('; ').find(row => row.startsWith(`${CONSENT_COOKIE}=true`));
    if (hasConsent) {
      TagManager.initialize({ gtmId: GTM_ID });
    }
  }, []);

  // page_view manual em cada troca de rota (inclusive o 1º carregamento).
  // page_location sem query: aqui ela pode ter e-mail/token (ex.: /auth/reset-password).
  useEffect(() => {
    track('page_view', {
      page_location: window.location.origin + pathname,
      page_title: document.title,
      tipo_pagina: 'app',
    });
  }, [pathname]);

  // Captura global de parâmetros na URL (evita que o ProtectedRoute limpe os parâmetros antes da leitura)
  useEffect(() => {
    if (search) {
      const urlParams = new URLSearchParams(search);
      const syncStatus = urlParams.get("google-sync");
      
      if (syncStatus) {
        // Limpa a URL imediatamente para não repetir o alerta ao navegar
        window.history.replaceState({}, document.title, pathname);

        if (syncStatus === "success") {
          if (authContext.setUser) {
            authContext.setUser(prev => ({ ...prev, is_google_connected: true }));
          }
          Swal.fire({
            icon: "success",
            title: "Agenda Sincronizada!",
            text: "Integração com o Google Calendar realizada com sucesso.",
          });
        } else if (syncStatus === "error") {
          Swal.fire({
            icon: "error",
            title: "Erro na Integração",
            text: "Não foi possível conectar ao Google Calendar.",
          });
        }
      }
    }
  }, [search, pathname, authContext]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();

  useEffect(() => {
    setupAxiosInterceptors(() => {
      // Verifica diretamente no window para garantir que o valor é o atual no momento da execução
      const currentPath = window.location.pathname;
      if (currentPath.includes("/auth/reset-password") || currentPath.includes("/auth/forgot-password") || currentPath.includes("/auth/register")) {
        return;
      }
      authContext.logout();
      navigate("/auth/login", { replace: true });
    });
  }, [authContext, navigate]);

  // Configuração padrão do tema (Sidenav White, Navbar Scroll, Light Mode)
  useEffect(() => {
    setWhiteSidenav(dispatch, true);
    setFixedNavbar(dispatch, false);
    setDarkMode(dispatch, false);
    setSidenavColor(dispatch, "custom");
  }, [dispatch]);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.type !== "auth") {
        return (
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        );
      }
      return null;
    });




  return (
    <>
      <GlobalStyles styles={{
        "input, select, textarea": {
          "@media (max-width: 768px)": {
            fontSize: "16px !important",
          },
        },
      }} />
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>

            <CssBaseline />
            <UpgradeModal />
            {layout === "dashboard" && (
              <>
                <MDBox display={{ xs: "none", md: "block" }}>
                  <Sidenav
                    color={sidenavColor}
                    brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandColor : brandWhite}
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                </MDBox>
                <BottomNav routes={routes} />
                <Configurator />
              </>
            )}
            {layout === "vr" && <Configurator />}
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="login" element={<Navigate to="/auth/login" />} />
              <Route path="register" element={<Navigate to="/auth/register" />} />
              <Route path="forgot-password" element={<Navigate to="/auth/forgot-password" />} />
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
            {layout === "dashboard" && (
              <MDBox sx={{ height: "80px", display: { xs: "block", md: "none" } }} />
            )}
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          <UpgradeModal />
          {layout === "dashboard" && (
            <>
              <MDBox display={{ xs: "none", md: "block" }}>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandColor : brandWhite}
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
              </MDBox>
              <BottomNav routes={routes} />
              <Configurator />
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            {getRoutes(routes)}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
          {layout === "dashboard" && (
            <MDBox sx={{ height: "80px", display: { xs: "block", md: "none" } }} />
          )}
        </ThemeProvider>
      )}
      <CookieConsent
        location="bottom"
        buttonText="Entendi e Aceito"
        cookieName={CONSENT_COOKIE}
        style={{ background: "#2E0249", zIndex: 9999, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "center" }}
        buttonStyle={{
          background: "linear-gradient(90deg, #00C6FF 0%, #0072FF 100%)",
          color: "#fff",
          fontSize: "14px",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "10px 24px"
        }}
        enableDeclineButton
        declineButtonText="Apenas Necessários"
        declineButtonStyle={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.5)",
          color: "#fff",
          fontSize: "14px",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "10px 24px",
          marginLeft: "10px"
        }}
        expires={365}
        onAccept={() => {
          TagManager.initialize({ gtmId: GTM_ID });
        }}
      >
        Este site utiliza cookies para garantir uma melhor experiência. Ao continuar navegando, você concorda com nossa <a href="https://djparafestas.com.br/politica-privacidade/" target="_blank" rel="noopener noreferrer" style={{ color: "#00C6FF", textDecoration: "none", fontWeight: "bold" }}>Política de Privacidade</a> e os <a href="https://djparafestas.com.br/termos-uso/" target="_blank" rel="noopener noreferrer" style={{ color: "#00C6FF", textDecoration: "none", fontWeight: "bold" }}>Termos de Uso</a>.
      </CookieConsent>
    </>
  );
}
